'use client'
import { useEffect, useState } from 'react'
import { IconMoodSad2 } from '@tabler/icons-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import qs from 'qs'

import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import Button from 'shared/ui/Button'
import SelectDropdown from 'shared/ui/form/Select'
import InfiniteScrollAnchor from 'shared/ui/InfiniteScrollAnchor'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { theme } from 'shared/ui/utils/designTokens'
import { UPSELLING_LISTING_LIMIT } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { viewProductList } from '../analytics'
import { getNextFrequentlyBoughtPage } from '../api/products'
import ProductCard from '../components/ProductCard'
import useInfiniteScroll from '../hooks/useInfiniteScroll'
import { ProductMetaTypes, ProductTypes } from '../types/products'
import { parseQueryFilters } from '../utils/filters'
import {
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValueForUpselling,
  SORTING_OPTIONS
} from '../utils/sorting'

import styles from './MostFrequentlyBoughtPage.module.scss'

const MOST_FREQUENTLY_BOUGHT_LIST_NAME =
  SeoListName.MOST_FREQUENTLY_BOUGHT_LIST_NAME

interface NajczesciejKupowaneClientProps {
  initialProducts: ProductTypes[]
  initialMeta: ProductMetaTypes
  token: string
  queryParams: Record<string, string | string[]>
}

export default function MostFrequentlyBoughtPage({
  initialProducts,
  token,
  queryParams
}: NajczesciejKupowaneClientProps) {
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [sorting, setSorting] = useState<string | string[]>('recommended')

  const { productsData, isFetching, ref } = useInfiniteScroll({
    products: initialProducts,
    query: queryParams,
    getNextPageCallback: getNextFrequentlyBoughtPage,
    token,
    limit: UPSELLING_LISTING_LIMIT
  })

  const handleSortingChange = async (_, value: string | string[]) => {
    setSorting(value)

    const sortingOptions = getSortingOptionsBySelectValueForUpselling(value)
    const currentParams = Object.fromEntries(searchParams?.entries() || [])
    const parsedQuery = parseQueryFilters(currentParams)

    if (value === 'recommended') {
      delete parsedQuery?.order
      delete parsedQuery?.sort_by
    }

    const params = qs.stringify(
      { ...parsedQuery, ...sortingOptions },
      { addQueryPrefix: true }
    )

    router.push(`${pathname}${decodeURIComponent(params)}`)
  }

  useEffect(() => {
    const currentParams = Object.fromEntries(searchParams?.entries() || [])
    const sortingToCheck = {
      sort_by: currentParams.sort_by,
      order: currentParams.order
    }

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }
  }, [searchParams, sorting])

  useEffect(() => {
    if (session?.status !== 'loading' && isNotNilOrEmpty(productsData)) {
      viewProductList({
        user,
        products: productsData || [],
        listName: MOST_FREQUENTLY_BOUGHT_LIST_NAME
      })
    }
  }, [session, productsData, user])

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>Najcześciej kupowane przez Ciebie</h1>
        <div className={styles.sortingInputWrapper} {...addTestId('sorting-input')}>
          <div className={styles.sortingLabel}>Sortuj:</div>
          <SelectDropdown
            id='most-frequently-bought-page-sorting'
            variant='ghost'
            noMargin
            options={SORTING_OPTIONS}
            name='sorting'
            value={sorting}
            onChange={handleSortingChange}
          />
        </div>
      </div>

      {initialProducts.length > 0 ? (
        <div className={styles.wrapper}>
          {productsData &&
            productsData.map((product: ProductTypes, index: number) => {
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  productPath={product.slug}
                  seoButtonPlace={pathname || ''}
                  listName={MOST_FREQUENTLY_BOUGHT_LIST_NAME}
                  idxOnList={index}
                  id={`most_frequently_bought_product_${product.id}`}
                />
              )
            })}
          <InfiniteScrollAnchor ref={ref} />
        </div>
      ) : (
        <div className={styles.emptyStateWrapper}>
          <IconMoodSad2
            width={80}
            height={80}
            color={theme.colors.text.disabled}
            strokeWidth={0.5}
          />
          <h2 className={styles.emptyStateHeader}>
            Nie kupiłeś jeszcze żadnego produktu
          </h2>
          <div>Przejdź do listy kategorii i znajdź to co Cię interesuje</div>
          <div className={styles.emptyStateButtonWrapper}>
            <Button
              id='profile_favourites_categories_button'
              variant='primary'
              onClick={() => {
                router.push(PATHS.categories)
              }}
            >
              Zobacz wszystkie kategorie
            </Button>
          </div>
        </div>
      )}

      {isFetching && (
        <div className={styles.loaderWrapper}>
          <LoadingIndicator />
        </div>
      )}
    </>
  )
}
